import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';
import CommonSelect from '../../Common/CommonSelect';
import CommonDate from '../../Common/CommonDate';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonInput from '../../Common/CommonInput';

import { fetchSettings } from '../../../store/modules/cashreceipt/cashreceiptActions';
import useFormSettings from '../../../hooks/useFormSettings';

import customerService from '../../../services/customerService';

const CashReceiptForm = ({ initialData, onSave, isLoading, onClose }) => {
    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('cashreceipt', fetchSettings);
    const { payment_methods, accounts } = settings || {};
    
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        receipt_number: '',
        customer: '',
        payment_date: '',
        payment_method: null,
        account: null,
        amount: '',
        reference: '',
        remarks: {},
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                receipt_number: initialData.receipt_number || '',
                customer: initialData.customer || null,
                payment_date: initialData.payment_date || '',
                payment_method: initialData.payment_method || null,
                account: initialData.account || null,
                amount: initialData.amount || '',
                reference: initialData.reference || '',
                remarks: initialData.remarks || {},
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isSubmitting = isLoading || isSaving;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Create Cash Receipt</h5>
                </div>
                <div className="d-flex justify-content-end">
                    <LanguageSwitch onLanguageChange={setSelectedLanguage} />
                </div>
            </div>
            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="receipt_number"
                            label="Receipt Number"
                            value={formData.receipt_number}
                            onChange={handleChange}
                            placeholder="Enter order number"
                            disabled={true}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonAsyncSelect
                            name="customer"
                            label="Customer"
                            value={formData.customer}
                            onChange={(value) => handleSelectChange('customer', value)}
                            loadOptions={async (inputValue) => {
                                
                                const condition = {
                                    rules: {
                                        condition: 'and',
                                        children: [
                                            {
                                                type: 'rule',
                                                query: {
                                                    operator: 'equal',
                                                    rule: 'users.customer_type',
                                                    type: 'text',
                                                    value: '1'
                                                }
                                            }
                                        ]
                                    }
                                }

                                const response = await customerService.getList({ search: inputValue }, condition);
                                return response.data.map(item => ({
                                    id: item.id,
                                    name: item.name || '',
                                    label: item.name?.en+' ('+item.customer_code+')'
                                }));
                            }}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="payment_date"
                            label="Payment Date"
                            value={formData.payment_date}
                            onChange={handleChange}
                            placeholder="Enter payment date"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="payment_method"
                            label="Payment Method"
                            value={formData.payment_method}
                            onChange={(value) => handleSelectChange('payment_method', value)}
                            options={payment_methods}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select payment method..."
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="account"
                            label="Account"
                            value={formData.account}
                            onChange={(value) => handleSelectChange('account', value)}
                            options={accounts}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select account..."
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="amount"
                            label="Amount"
                            value={formData.amount}
                            onChange={handleChange}
                            placeholder="Enter amount"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="reference"
                            label="Reference"
                            value={formData.reference}
                            onChange={handleChange}
                            placeholder="Enter reference"
                            disabled={isSubmitting}
                            errors={errors}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Remarks"
                            name="remarks"
                            value={formData.remarks}
                            onChange={handleChange}
                            errors={errors}
                            required={false}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            rows={1}
                        />
                    </div>
                </div>
            </div>

            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading...
                        </>
                    ) : isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
        </form>
    );
};

export default CashReceiptForm; 